import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "./CustomerAccountDetail.module.css";
import WebService from "../Services/WebService";
import Paginations from "../Customs/pagination/Paginations";
import RecordPaymentModal from "./RecordPaymentModal";
import CustomerPanel from "./CustomerPanel";
import LedgerStatement from "./LedgerStatement";
import TransactionDetailModal, { TransactionDetailTarget } from "./TransactionDetailModal";
import { FaPen } from "react-icons/fa";
import { useBladeBack, useInBlade } from "../Customs/FullWidthBlade/FullWidthBlade";

interface CustomerAccountDetailProps {
  userId: number | null;
}

const PAGE_SIZE = 8;
const FILTER_OPTIONS = [
  { label: "Last Week", value: "last_week" },
  { label: "Last Month", value: "last_month" },
  { label: "Last 3 Months", value: "last_3_months" },
  { label: "Last Year", value: "last_year" },
  { label: "Custom", value: "custom" },
];


const PAYMENT_STATUS_OPTIONS = [
  { value: "PARTIAL_PAYMENT", label: "Partial Payment" },
  { value: "PENDING_PAYMENT", label: "Pending Payment" },
  { value: "PAID", label: "Paid" }
];
const STATUS_LABEL_MAP: Record<string, string> = {
  PARTIAL_PAYMENT: "Partial Payment",
  PENDING_PAYMENT: "Pending Payment",
  PAID: "Paid",
  DRAFT: "Checkout",
  VOIDED: "Reversed"
};
function formatAccountDate(value: unknown): string {
  if (value == null) return "--";
  const raw = String(value).trim();
  if (!raw || raw === "0001-01-01T00:00:00Z" || raw.toLowerCase() === "null") {
    return "--";
  }
  // yyyy-MM-dd (or prefix of ISO) — parse as local date to avoid timezone shift
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (ymd) {
    const d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "--";
  return d.toLocaleDateString();
}
// Only return from_date for preset filters
function getFromDate(filterValue: string): string {
  const today = new Date();
  let fromDateObj = new Date(today);

  if (filterValue === "last_week") {
    fromDateObj.setDate(today.getDate() - 7);
  } else if (filterValue === "last_month") {
    fromDateObj.setMonth(today.getMonth() - 1);
  } else if (filterValue === "last_3_months") {
    fromDateObj.setMonth(today.getMonth() - 3);
  } else if (filterValue === "last_year") {
    fromDateObj.setFullYear(today.getFullYear() - 1);
  } else {
    return "";
  }
  return fromDateObj.toISOString().slice(0, 10);
}

const CustomerAccountDetail: React.FC<CustomerAccountDetailProps> = ({
  userId,
}) => {
  const [customer, setCustomer] = useState<any>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [editPanelOpen, setEditPanelOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<"orders" | "ledger">(
    () => (searchParams.get("view") === "ledger" ? "ledger" : "orders")
  );
  const [ledgerRefreshKey, setLedgerRefreshKey] = useState(0);
  const [transactionDetail, setTransactionDetail] = useState<TransactionDetailTarget | null>(null);
  const [orderDateFilter, setOrderDateFilter] = useState("last_year");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [customFromDate, setCustomFromDate] = useState("");
  const [customToDate, setCustomToDate] = useState("");
  const searchRef = useRef<NodeJS.Timeout | null>(null);
  const handleBack = useBladeBack();
  const inBlade = useInBlade();

  // Fetch data (with payment status filter)
  const fetchCustomerDetail = () => {
    if (!userId) return;
    setLoading(true);

    let params = [
      `limit=${PAGE_SIZE}`,
      `page_number=${page}`,
      `order_like=${search}`,
    ];

    // Handle date filters
    if (orderDateFilter === "custom") {
      // Custom: only add if dates are valid
      if (customFromDate && customToDate) {
        // Validate: from_date must be <= to_date
        if (new Date(customFromDate) <= new Date(customToDate)) {
          params.push(`from_date=${customFromDate}`);
          params.push(`to_date=${customToDate}`);
        }
      } else if (customFromDate) {
        params.push(`from_date=${customFromDate}`);
      }
    } else if (orderDateFilter) {
      // Preset filters: only pass from_date
      const from = getFromDate(orderDateFilter);
      if (from) params.push(`from_date=${from}`);
    }

    if (paymentStatus) params.push(`payment_statuses=${paymentStatus}`);

    WebService.getAPI({
      action: `/v1/door-management/accounts/${userId}?${params.join("&")}`,
    })
      .then((res: any) => {
        const c = res.data?.accounts || null;
        setCustomer(c);
        setOrders(c?.orders_details || []);
        setTotalPages(res.data?.pagination?.total_pages || 1);
      })
      .catch(() => {
        setCustomer(null);
        setOrders([]);
        setTotalPages(1);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCustomerDetail();
    // eslint-disable-next-line
  }, [userId, page, search, orderDateFilter, paymentStatus, customFromDate, customToDate]);

  useEffect(() => {
    setActiveTab(searchParams.get("view") === "ledger" ? "ledger" : "orders");
    // Only when switching customers — don't override an in-page tab click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
    if (searchRef.current) clearTimeout(searchRef.current);
    searchRef.current = setTimeout(() => {}, 350);
  };

  if (!userId) return null;

  // Prepare customer info for badges
  const company = (customer?.company_name != null ? String(customer.company_name) : "").trim();
  const person = (
    customer?.customer_name != null
      ? String(customer.customer_name)
      : customer?.username != null
      ? String(customer.username)
      : ""
  ).trim();
  const companyClean = !company || company.toLowerCase() === "null" ? "" : company;
  const personClean = !person || person.toLowerCase() === "null" ? "" : person;
  const customerName =
    companyClean && personClean
      ? `${companyClean} (${personClean})`
      : companyClean || personClean || "--";
  const credit = customer?.credit_limit != null && customer?.credit_limit !== "" ? `₹${customer.credit_limit}` : "--";
  const remainingCredit = customer?.credit_remaining != null && customer?.credit_remaining !== ""
    ? `₹${customer.credit_remaining}`
    : null;
  const dueAmt = customer?.overall_due_amount != null && customer?.overall_due_amount !== ""
    ? `₹${customer.overall_due_amount}`
    : "--";
  const unappliedCredit = customer?.unapplied_credit != null && Number(customer.unapplied_credit) > 0
    ? `₹${customer.unapplied_credit}`
    : null;
  const policy = customer?.policy || "Monthly";

  const handleRefund = async () => {
    const available = Number(customer?.unapplied_credit ?? 0);
    if (!userId || !(available > 0)) return;
    const raw = window.prompt(`Refund amount (unapplied credit ₹${available})`, String(available));
    if (raw == null) return;
    const amount = Number(raw);
    if (!Number.isFinite(amount) || amount <= 0) {
      return;
    }
    try {
      await WebService.postAPI({
        action: "/v1/door-management/accounts/refund",
        body: { user_id: userId, amount },
      });
      fetchCustomerDetail();
      setLedgerRefreshKey((k) => k + 1);
    } catch (e: any) {
      // keep existing toast-less error style used elsewhere on this page
      window.alert(typeof e === "string" ? e : e?.message || "Refund failed");
    }
  };

  return (
    <div
      className={styles.pageWrapper}
      style={inBlade ? { width: "100%", maxWidth: "100%", margin: 0, minHeight: "100%" } : undefined}
    >
      {/* Back Button */}
      <button
        className={styles.backBtn}
        style={{ margin: "20px 0 0 10px" }}
        onClick={handleBack}
      >
        ← Back
      </button>

      {/* Top header */}
      <div className={styles.headerRow} style={{ marginBottom: 4 }}>
        <div>
          <h1 className={styles.pageTitle}>Customer Details</h1>
          <div className={styles.subtext}>Manage customer payments and orders</div>
        </div>
        <div className={styles.headerControls}>
          <input
            type="number"
            className={styles.searchInput}
            placeholder="Search order..."
            value={search}
            onChange={handleSearch}
          />
          {/* Date filter dropdown */}
          <select
            className={styles.filterDropdownSmall}
            value={orderDateFilter}
            onChange={e => {
              setOrderDateFilter(e.target.value);
              setPage(1);
            }}
            style={{ minWidth: 135, marginRight: 8 }}
          >
            {FILTER_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {/* Custom date inputs (only show when Custom is selected) */}
          {orderDateFilter === "custom" && (
            <>
              <input
                type="date"
                className={styles.filterDropdownSmall}
                value={customFromDate}
                onChange={e => {
                  setCustomFromDate(e.target.value);
                  setPage(1);
                }}
                max={customToDate || undefined}
                placeholder="From date"
                style={{ minWidth: 135, marginRight: 8 }}
              />
              <input
                type="date"
                className={styles.filterDropdownSmall}
                value={customToDate}
                onChange={e => {
                  setCustomToDate(e.target.value);
                  setPage(1);
                }}
                min={customFromDate || undefined}
                placeholder="To date"
                style={{ minWidth: 135, marginRight: 8 }}
              />
            </>
          )}
          {/* Payment Status filter dropdown */}
          <select
            className={styles.filterDropdownSmall}
            value={paymentStatus}
            onChange={e => {
              setPaymentStatus(e.target.value);
              setPage(1);
            }}
            style={{ minWidth: 155, marginRight: 12 }}
          >
            <option value="">All Status</option>
            {PAYMENT_STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            className={styles.recordPaymentBtn}
            onClick={() => setPaymentModalOpen(true)}
          >
            + Record Payment
          </button>
        </div>
      </div>

      {/* Customer Info Bar (edit icon far right) */}
      <div className={styles.customerInfoBar}>
        <div className={styles.customerInfoLeft}>
          <span className={styles.customerName}>{customerName}</span>
          <span className={styles.policyBadge}>{policy}</span>
          <span className={styles.creditBadge}>Credit: {credit}</span>
          {remainingCredit && (
            <span className={styles.creditBadge}>Remaining: {remainingCredit}</span>
          )}
          <span className={styles.dueBadge}>Due Amt: {dueAmt}</span>
          {unappliedCredit && (
            <>
              <span className={styles.creditBadge}>Unapplied: {unappliedCredit}</span>
              <button type="button" className={styles.recordPaymentBtn} onClick={handleRefund}>
                Refund credit
              </button>
            </>
          )}
        </div>
        <button
          className={styles.editIconBtn}
          onClick={() => setEditPanelOpen(true)}
          title="Edit"
          type="button"
        >
          <FaPen />
          <span className={styles.editBtnText}>Edit</span>
        </button>
      </div>

      <div className={styles.tabRow}>
        <button
          type="button"
          className={`${styles.tabBtn} ${activeTab === "orders" ? styles.tabBtnActive : ""}`}
          onClick={() => setActiveTab("orders")}
        >
          Orders
        </button>
        <button
          type="button"
          className={`${styles.tabBtn} ${activeTab === "ledger" ? styles.tabBtnActive : ""}`}
          onClick={() => setActiveTab("ledger")}
        >
          Ledger
        </button>
      </div>

      {activeTab === "ledger" ? (
        <LedgerStatement
          partyType="CLIENT"
          partyId={Number(userId)}
          refreshKey={ledgerRefreshKey}
          customerName={customerName}
        />
      ) : (
      <>
      {/* Orders Table */}
<div className={styles.ordersTableWrapper}>
  {loading ? (
    <div className={styles.emptyState}>Loading...</div>
  ) : orders.length === 0 ? (
    <div className={styles.emptyState}>
      <div className={styles.emptyTitle}>No orders found</div>
      <div className={styles.emptyNote}>Try changing the date range or status filter.</div>
    </div>
  ) : (
    <table className={styles.ordersTable}>
      <thead>
        <tr>
          <th>Order ID</th>
          <th>Order Date</th>
          <th>Due Date</th>
          <th>Total Amount</th>
          <th>Amount Paid</th>
          <th>Amount Due</th>
          <th>Order Status</th>
          <th>Payment Status</th>
        </tr>
      </thead>
      <tbody>
        {orders.map((order: any, idx: number) => {
          const paymentStatusKey =
            order.current_status || order.payment_status || "";
          const label =
            STATUS_LABEL_MAP[paymentStatusKey] || paymentStatusKey || "Other";
          return (
            <tr key={order.sale_order_id || idx}>
              <td>
                {order.sale_order_id ? (
                  <button
                    type="button"
                    className={styles.orderLink}
                    onClick={() =>
                      setTransactionDetail({
                        side: "invoice",
                        link: {
                          kind: "ORDER",
                          number: `SO-${order.sale_order_id}`,
                          sale_order_id: order.sale_order_id,
                          amount: order.final_amount ?? order.total_amount,
                        },
                        customerName,
                      })
                    }
                  >
                    #{order.sale_order_id}
                  </button>
                ) : (
                  "--"
                )}
              </td>
              <td>{formatAccountDate(order.order_date || order.created_at)}</td>
              <td>
                {formatAccountDate(
                  order.due_date || order.expected_delivery_date
                )}
              </td>
              <td>₹{order.total_amount ?? order.final_amount ?? "--"}</td>
              <td>₹{order.total_paid_amount ?? order.paid_amount ?? "--"}</td>
              <td>₹{order.due_amount ?? "--"}</td>
              <td>{order.status || "--"}</td>
              <td>
                <span
                  className={`${styles.statusBadge} ${
                    paymentStatusKey === "PARTIAL_PAYMENT"
                      ? styles.statusPartial
                      : paymentStatusKey === "PENDING_PAYMENT"
                      ? styles.statusPending
                      :                     paymentStatusKey === "PAID"
                      ? styles.statusPaid
                      : paymentStatusKey === "VOIDED"
                      ? styles.statusVoided
                      : paymentStatusKey === "DRAFT"
                      ? styles.statusDraft
                      : ""
                  }`}
                >
                  {label}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  )}
</div>

      {/* Pagination */}
      {!loading && orders.length > 0 && (
        <div className={styles.paginationRow}>
          <Paginations
            totalItems={totalPages * PAGE_SIZE}
            itemsCountPerPage={PAGE_SIZE}
            changePage={setPage}
            changeCurrentPage={page}
          />
        </div>
      )}
      </>
      )}

      {transactionDetail && (
        <TransactionDetailModal
          open={transactionDetail != null}
          target={transactionDetail}
          onClose={() => setTransactionDetail(null)}
        />
      )}

      {/* Record Payment Modal */}
      {paymentModalOpen && (
        <RecordPaymentModal
          open={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          selectedCustomerId={userId}
          customerDropdownDisabled={true}
          outstanding={customer?.overall_due_amount}
          credit={customer?.credit_limit}
          onSuccess={() => {
            fetchCustomerDetail();
            setLedgerRefreshKey((k) => k + 1);
            setPaymentModalOpen(false);
          }}
        />
      )}

      {/* Edit Panel */}
      {editPanelOpen && (
        <CustomerPanel
          open={editPanelOpen}
          mode="edit"
          userId={userId ?? undefined}
          onClose={() => setEditPanelOpen(false)}
          onSaved={() => {
            setEditPanelOpen(false);
            fetchCustomerDetail();
          }}
        />
      )}
    </div>
  );
};

export default CustomerAccountDetail;
